import { Combobox } from '@headlessui/react';
import { cleanBookTitle } from '../Contexts/DiffitBooksContext';
import { Transition } from '@headlessui/react';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import Image from '../../../components/Image';
import { BOOKS_ID, usePacketInput } from '../Contexts/PacketInputContext';
import { Mixpanel } from '../../../services/Mixpanel';
import { useDiffitBooks } from '../Contexts/DiffitBooksContext';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

export const SearchBooksCombobox = () => {
  const { book, setBook, inputType, setStartChapter, setEndChapter } = usePacketInput();
  const { diffitBooks = [] } = useDiffitBooks();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);

  // Memoize filtered books for performance
  const booksInSearch = useMemo(() => {
    if (!searchQuery) return diffitBooks.slice(0, 100);
    const lowerCaseQuery = searchQuery.toLowerCase().replace(/[^a-z0-9]/g, '');
    return diffitBooks
      .filter(
        (book) =>
          book.title
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .includes(lowerCaseQuery) ||
          book.author
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .includes(lowerCaseQuery)
      )
      .slice(0, 100);
  }, [diffitBooks, searchQuery]);

  // Debounced Mixpanel tracking
  const debouncedMixpanelTrack = useCallback(
    debounce((event, props) => {
      Mixpanel.track(event, props);
    }, 1000),
    []
  );

  // Auto-focus input and show dropdown when books tab becomes active
  useEffect(() => {
    if (inputType.id === BOOKS_ID && inputRef.current) {
      inputRef.current.focus();
      setShowDropdown(true);
    }
  }, [inputType.id]);

  if (inputType.id !== BOOKS_ID)
    return (
      <div>
        <span>No books</span>
      </div>
    );

  const selectBook = useCallback(
    (selectedBook) => {
      setBook(selectedBook);
      setStartChapter(-1);
      setEndChapter(-1);
    },
    [setBook, setStartChapter, setEndChapter]
  );

  return (
    <div className='pt-1'>
      <div className='relative flex flex-row w-full'>
        <Combobox
          className='w-full text-sm'
          as='div'
          value={book?.title ? cleanBookTitle(book.title) : undefined}>
          {({ activeOption }) => (
            <>
              <div className='flex items-center relative sr-only'>
                <Combobox.Label id='tradebook-search-label'>Search for a Book</Combobox.Label>
              </div>

              <Combobox.Input
                ref={inputRef}
                id={'tradebook-search-input'}
                className={`rounded-xl w-full px-2 pt-5 pb-8 border-none focus:ring-0 placeholder:text-gray-400 placeholder:font-light caret-emerald-600`}
                placeholder={
                  diffitBooks.length === 0 ? 'Loading...' : 'Search for a title or author'
                }
                value={book?.title ? cleanBookTitle(book.title) : searchQuery}
                onChange={(e) => {
                  selectBook(null);
                  setSearchQuery(e.target.value);
                  debouncedMixpanelTrack('Searched Books', { query: e.target.value });
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={(e) => {
                  const relatedTargetId = e.relatedTarget?.id || '';
                  if (!relatedTargetId.includes('combobox-option')) {
                    setShowDropdown(false);
                  }
                }}
                autoComplete='off'
                onKeyDown={(e) => {
                  if (!showDropdown) setShowDropdown(true);
                  if (e.key === 'Enter') {
                    if (activeOption === requestABookStr) {
                      e.preventDefault();
                      Mixpanel.track('Requested A Book', { type: 'keyboard' });
                      window.open(requestABookUrl, '_blank');
                    } else if (activeOption) {
                      debouncedMixpanelTrack?.cancel();
                      Mixpanel.track('Selected Book', { book: activeOption });
                      selectBook(activeOption);
                      setShowDropdown(false);
                    }
                  }
                }}
              />
              <Transition show={showDropdown}>
                <Combobox.Options
                  static
                  id='list-of-books'
                  style={{
                    // to give the illusion that the dropdown is as wide as the InputContainer
                    width: 'calc(100% + 34px)',
                  }}
                  className='absolute -left-[17px] -translate-y-2 bg-white z-[10] max-h-[240px] border border-gray-400 rounded-b-lg empty:hidden overflow-auto shadow-lg scrollbar-minimal'>
                  {booksInSearch.map((currentBook) => {
                    const cleanedTitle = cleanBookTitle(currentBook.title);
                    return (
                      <Combobox.Option
                        key={currentBook.title + '--' + currentBook.author}
                        value={currentBook}
                        name={currentBook.title}
                        className={({ active }) =>
                          `flex items-center h-18 border-b border-gray-200 w-full hover:bg-gray-50 cursor-pointer ${
                            active ? 'bg-gray-50 ' : 'text-gray-900'
                          }`
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          Mixpanel.track('Selected Book', { book: cleanedTitle });
                          if (activeOption !== requestABookStr) {
                            debouncedMixpanelTrack?.cancel();
                            selectBook(currentBook);
                          }
                          setShowDropdown(false);
                        }}>
                        <div className='flex-1 flex py-1 items-center w-full h-full'>
                          <span className='w-11 h-14 ms-4 p-1.5 flex items-center'>
                            <Image
                              src={currentBook.cover}
                              alt={cleanedTitle}
                              className='h-fit w-10 object-contain'
                            />
                          </span>
                          <div className='flex flex-col flex-1 pe-1 ml-4'>
                            <span className='truncate font-medium text-gray-900'>
                              {cleanedTitle}
                            </span>
                            <span className='truncate font-normal text-gray-500'>
                              {currentBook.author}
                            </span>
                          </div>
                        </div>
                      </Combobox.Option>
                    );
                  })}
                  <RequestABookOption />
                </Combobox.Options>
              </Transition>
            </>
          )}
        </Combobox>
      </div>
    </div>
  );
};

const requestABookStr = 'Request A Book';
const requestABookUrl = 'https://forms.gle/HVNp2QBV4hPHKkpT8';
const RequestABookOption = () => {
  const keyStr = requestABookStr;
  return (
    <Combobox.Option
      key={keyStr}
      value={keyStr}
      name={keyStr}
      className={({ active }) =>
        `'group flex cursor-pointer items-center py-2 px-3 h-full w-full hover:bg-gray-100 text-emerald-700 ' ${
          active ? 'bg-gray-100 ' : 'text-emerald-700'
        }`
      }
      onClick={() => {
        window.open(requestABookUrl, '_blank');
        Mixpanel.track('Requested A Book', { type: 'click' });
      }}>
      <div className='w-full text-left py-2 ms-1'>
        <span className='flex items-center pointer-events-none'>
          Don't see your book? Request it
          <ChevronRightIcon className='h-4 w-4 ml-0.5 text-emerald-700' />
        </span>
      </div>
    </Combobox.Option>
  );
};
